import { useEffect, useMemo, useState } from 'react'
import { RankmiTable } from '@rankmi/rankmi-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEllipsisV,
  faHourglassHalf,
  faVideo,
  faVideoSlash,
  faFileCertificate,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { Button, Menu, Dropdown, Tooltip, Input } from 'antd'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { slices } from 'store/slices'
import Wrapper from 'components/reusables/wrapper'
import getWebinars from 'api/webinars/getWebinars'
import { IWebinar } from 'interfaces/webinars'
import { useIsMobile } from 'hooks/useIsMobile'
import { IPanelist } from '../../interfaces/webinars'
import {
  Title,
  Description,
  RankmiTableWrapper,
  Options,
  EmptyWebinarContainer,
  EmptyWebinarIcon,
  EmptyWebinarText,
  WebinarName,
  WebinarCourseName,
  SoonButton,
  StartWebinarButton,
  BadgeResponsiveWebinars,
  ZoomSyncAlertButton,
  WebinarTableActionsRow,
} from './styled'
import { ModalPanelist } from './modal'
import { WebinarMobileCard } from './mobile/WebinarMobileCard'
import { disablePanelistAccess, formatWebinarTimeRange } from './helpers'

export const Webinars = () => {
  const { t } = useTranslation()
  const prefix = 'webinars'
  const history = useHistory()
  const dispatch = useAppDispatch()
  const webinars = useAppSelector(slices.webinars.items)
  const loadingWebinars = useAppSelector(slices.webinars.loading)
  const loadedWebinars = useAppSelector(slices.webinars.loaded)
  const [panelList, setPanelList] = useState<IPanelist[]>([])
  const [panelistModal, setPanelistModal] = useState(false)
  const [searchWebinar, setSearchWebinar] = useState('')
  const isMobile = useIsMobile()

  useEffect(() => {
    if (!loadedWebinars && !loadingWebinars) getData()
  }, [])

  const getData = async () => {
    dispatch(slices.webinars.setLoading(true))
    await getWebinars().then((response) => {
      dispatch(slices.webinars.setItems(response))
    })
    dispatch(slices.webinars.setLoading(false))
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.table.rowsCount`),
      rowsCount_plural: t(`${prefix}.table.rowsCountPlural`),
    },
  }

  const columns = [
    {
      title: `${t(`${prefix}.table.webinarDate`)}`,
      dataIndex: 'startTime',
      key: 'startTime',
      width: 110,
      render: (startTime: string) => (
        <div>{format(new Date(startTime), 'dd/MMM/yyyy', { locale: es })}</div>
      ),
    },
    {
      title: `${t(`${prefix}.table.webinarName`)}`,
      hideable: false,
      sorteable: false,
      render: (webinar: IWebinar) => (
        <div>
          <WebinarName>{webinar.title}</WebinarName>
          <WebinarCourseName>
            {t(`${prefix}.table.courseName`)}: {webinar.course}
          </WebinarCourseName>
        </div>
      ),
    },
    {
      title: `${t(`${prefix}.table.startTimeAndEndTimeWebinar`)}`,
      width: 200,
      align: 'center',
      render: (webinar: IWebinar) =>
        formatWebinarTimeRange(webinar.startTime, webinar.duration),
    },
    {
      title: `${t(`${prefix}.table.participantCount`)}`,
      dataIndex: 'userCount',
      key: 'userCount',
      width: 110,
      align: 'center',
      render: (userCount: number) => userCount,
    },
    {
      width: 200,
      hideable: false,
      align: 'center',
      fixed: 'right',
      render: (webinarData: IWebinar) => (
        <WebinarTableActionsRow>
          {webinarData.hasIntegrationResponse === false && (
            <Tooltip title={t(`${prefix}.table.tooltipZoomSyncFailed`)}>
              <ZoomSyncAlertButton>
                <Button
                  type="default"
                  aria-label={t(`${prefix}.table.tooltipZoomSyncFailed`)}
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} size="xs" />
                </Button>
              </ZoomSyncAlertButton>
            </Tooltip>
          )}
          {!disablePanelistAccess(webinarData.startTime) ? (
            <a target="_blank" href={webinarData.hostUrl}>
              <StartWebinarButton>
                <Button type="primary">
                  {t(`${prefix}.table.btnStartWebinar`)}
                </Button>
              </StartWebinarButton>
            </a>
          ) : (
            <SoonButton>
              <Tooltip title={t(`${prefix}.table.tooltipBtnSoonWebinar`)}>
                <Button>
                  <FontAwesomeIcon
                    icon={faHourglassHalf}
                    size="xs"
                    style={{ paddingRight: '8px' }}
                  />
                  {t(`${prefix}.table.btnSoonWebinar`)}
                </Button>
              </Tooltip>
            </SoonButton>
          )}
          <Dropdown
            placement="bottomRight"
            overlay={
              <Menu>
                <Menu.Item
                  disabled={disablePanelistAccess(webinarData.startTime)}
                  onClick={() => {
                    setPanelList(webinarData.panelists || [])
                    setPanelistModal(true)
                  }}
                >
                  <Tooltip
                    title={t(`${prefix}.table.actions.tooltipBtnPanelistAcess`)}
                  >
                    {t(`${prefix}.table.actions.btnPanelistAccess`)}
                  </Tooltip>
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    history.push(
                      `/administration/monitoring/courses/${webinarData.courseId}`
                    )
                  }
                >
                  {t(`${prefix}.table.actions.btnMonitoringCourse`)}
                </Menu.Item>
              </Menu>
            }
          >
            <Options>
              <Button type="link">
                <FontAwesomeIcon icon={faEllipsisV} />
              </Button>
            </Options>
          </Dropdown>
        </WebinarTableActionsRow>
      ),
    },
  ]

  const filteredWebinars = useMemo(() => {
    return webinars.filter((webinar) =>
      webinar.title.toLowerCase().includes(searchWebinar.toLowerCase())
    )
  }, [webinars, searchWebinar])

  return (
    <Wrapper shadow={true}>
      <Title>{t(`${prefix}.title`)}</Title>
      <Description>{t(`${prefix}.description`)}</Description>
      {webinars.length === 0 && !loadingWebinars && loadedWebinars ? (
        <EmptyWebinarContainer>
          <EmptyWebinarIcon>
            <div className="iconWrapper">
              <FontAwesomeIcon icon={faVideoSlash} size="2x" />
            </div>
          </EmptyWebinarIcon>
          <EmptyWebinarText>{t(`${prefix}.emptyWebinars`)}</EmptyWebinarText>
        </EmptyWebinarContainer>
      ) : (
        <>
          {!isMobile ? (
            <RankmiTableWrapper>
              <RankmiTable
                columnHeaders={columns}
                data={webinars}
                countIcon={faVideo}
                total={webinars.length}
                locale={locale}
                loading={loadingWebinars}
                responsive
              />
            </RankmiTableWrapper>
          ) : (
            <>
              <Input
                placeholder={t(`${prefix}.table.inputSearchWebinar`)}
                value={searchWebinar}
                onChange={(e) => setSearchWebinar(e.target.value)}
                style={{ marginBottom: '20px' }}
                suffix={<FontAwesomeIcon icon={faSearch} color="#AAAAAA" />}
              />
              <BadgeResponsiveWebinars>
                <FontAwesomeIcon
                  icon={faFileCertificate}
                  style={{ paddingRight: '4px' }}
                />
                {webinars.length}{' '}
                {webinars.length === 1
                  ? t(`${prefix}.table.rowsCount`)
                  : t(`${prefix}.table.rowsCountPlural`)}
              </BadgeResponsiveWebinars>
              {filteredWebinars.map((webinar) => (
                <WebinarMobileCard webinar={webinar} key={webinar.id} />
              ))}
            </>
          )}
        </>
      )}
      <ModalPanelist
        show={panelistModal}
        close={() => setPanelistModal(false)}
        panelList={panelList}
      />
    </Wrapper>
  )
}
